import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import moment from "moment";

const INACTIVITY_SECS = 5 * 60;
const UNRESPONSIVE_SECS = 60 * 60;
const LOW_BATTERY_PCT = 95;

function CardSpinner() {
    return (
        <div style={{
            position: "absolute", inset: 0, background: "rgba(255,255,255,0.8)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10, borderRadius: "inherit",
        }}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: "2rem", color: "#6c757d" }} />
        </div>
    );
}

function AIAnalysisCard({ deviceData, maintenanceSchedules, shifts, loading }) {
    const [collapsed, setCollapsed] = useState(false);

    const insights = useMemo(() => {
        const items = [];
        const devices = deviceData?.shadowDevices || [];
        const jobScheduleCount = deviceData?.aggregateJobSchedule?._count?.id ?? 0;
        const now = moment();
        const currentSec = now.hours() * 3600 + now.minutes() * 60 + now.seconds();
        const todayDow = now.day();

        // Device health
        let offline = 0, lowBatt = 0, activeJobs = 0;
        const partsInProd = new Set();
        devices.forEach((d) => {
            const secsAgo = moment().diff(moment(d?.device?.lastSeen?.master || 0), "seconds");
            if (secsAgo >= UNRESPONSIVE_SECS) offline++;
            const batt = d?.device?.runtime?.attributes?.battery_level;
            if (batt != null && batt < LOW_BATTERY_PCT) lowBatt++;
            (d.jobSchedules || []).forEach((js) => {
                if (currentSec >= js.start_time && currentSec <= js.end_time) {
                    activeJobs++;
                    if (js.part?.name) partsInProd.add(js.part.name);
                }
            });
        });

        if (offline > 0) {
            items.push({
                icon: "fa-wifi text-danger",
                text: `${offline} device${offline !== 1 ? "s have" : " has"} not reported in over 1 hour.`,
                link: "/devices",
                linkText: "Inspect devices",
                severity: "danger",
            });
        } else if (devices.length > 0) {
            items.push({
                icon: "fa-check-circle text-success",
                text: `All ${devices.length} device${devices.length !== 1 ? "s are" : " is"} reporting normally.`,
                severity: "success",
            });
        }

        if (lowBatt > 0) {
            items.push({
                icon: "fa-battery-quarter text-warning",
                text: `${lowBatt} device${lowBatt !== 1 ? "s are" : " is"} reporting low battery (below ${LOW_BATTERY_PCT}%).`,
                link: "/devices",
                linkText: "View devices",
                severity: "warning",
            });
        }

        if (activeJobs > 0) {
            items.push({
                icon: "fa-industry text-primary",
                text: `${activeJobs} job${activeJobs !== 1 ? "s are" : " is"} running right now across ${partsInProd.size} part${partsInProd.size !== 1 ? "s" : ""}.`,
                severity: "info",
            });
        } else if (jobScheduleCount > 0) {
            items.push({
                icon: "fa-calendar text-muted",
                text: `No jobs are running at this moment. ${jobScheduleCount} schedule${jobScheduleCount !== 1 ? "s are" : " is"} configured.`,
                link: "/job-schedules",
                linkText: "View schedules",
                severity: "secondary",
            });
        }

        // Maintenance due soon
        const dueSoon = (maintenanceSchedules || []).filter((ms) => {
            let attrs = ms.schedule_attributes;
            if (typeof attrs === "string") { try { attrs = JSON.parse(attrs); } catch { attrs = {}; } }
            const from = attrs?.from ? moment(attrs.from) : null;
            if (!from) return false;
            const days = from.startOf("day").diff(moment().startOf("day"), "days");
            return days >= 0 && days <= 7;
        }).length;

        if (dueSoon > 0) {
            items.push({
                icon: "fa-cogs text-warning",
                text: `${dueSoon} maintenance event${dueSoon !== 1 ? "s are" : " is"} due within the next 7 days.`,
                link: "/maintenance",
                linkText: "View schedule",
                severity: "warning",
            });
        }

        // Shift coverage
        if (shifts?.length) {
            const activeShifts = shifts.filter((s) => {
                if (s.runs_on && !(s.runs_on & (1 << todayDow))) return false;
                const end = s.end_time < s.start_time ? 86400 : s.end_time;
                return currentSec >= s.start_time && currentSec <= end;
            });
            if (activeShifts.length > 0) {
                items.push({
                    icon: "fa-user-clock text-info",
                    text: `Currently in ${activeShifts.map((s) => `"${s.name}"`).join(", ")} shift${activeShifts.length !== 1 ? "s" : ""}.`,
                    severity: "info",
                });
            }
        }

        return items;
    }, [deviceData, maintenanceSchedules, shifts]);

    return (
        <div className="card card-outline dashboard-strip-card mb-3" style={{ borderColor: "#7c3aed", position: "relative" }}>
            {loading && <CardSpinner />}
            <div
                className="card-header"
                style={{
                    background: "linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)",
                    color: "#ffffff",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                <div style={{ position: "relative", zIndex: 1 }}>
                    <h3 className="card-title tw-font-bold">
                        <i className="fas fa-chart-line mr-2" style={{ color: "#ffffff" }} />
                        Live Insights
                        <span
                            className="badge badge-light badge-pill ml-2"
                            style={{ background: "rgba(255,255,255,0.9)", color: "#7c3aed", fontWeight: "bold" }}
                        >
                            {insights.length} observation{insights.length !== 1 ? "s" : ""}
                        </span>
                    </h3>
                </div>
                <i className="fas fa-star" style={{ position: "absolute", top: "10px", right: "100px", color: "rgba(255,255,255,0.5)", fontSize: "10px" }} />
                <i className="fas fa-star" style={{ position: "absolute", bottom: "12px", right: "160px", color: "rgba(255,255,255,0.35)", fontSize: "7px" }} />
                <style>{`@keyframes twinkle { 0%, 100% { opacity: 0.3; } 50% { opacity: 0.9; } }`}</style>
                <div className="card-tools">
                    <button
                        type="button"
                        className="btn btn-tool"
                        style={{ color: "#ffffff" }}
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        <i className={`fas ${collapsed ? "fa-plus" : "fa-minus"}`} />
                    </button>
                </div>
            </div>
            <div
                className={`card-body ${collapsed ? "d-none" : ""}`}
                style={{ background: "linear-gradient(180deg, rgba(124,58,237,0.04) 0%, rgba(99,102,241,0.02) 100%)" }}
            >
                {insights.length === 0 ? (
                    <p className="text-muted text-center small mb-0 py-2">Loading observations…</p>
                ) : (
                    <div className="d-flex flex-column" style={{ gap: 6 }}>
                        {insights.map((item, i) => (
                            <div
                                key={i}
                                className="d-flex align-items-start p-2 rounded"
                                style={{ background: "rgba(255,255,255,0.7)", border: "1px solid rgba(0,0,0,0.07)" }}
                            >
                                <i className={`fa ${item.icon} mt-1 mr-2 flex-shrink-0`} style={{ fontSize: "0.85rem" }} />
                                <span style={{ fontSize: "0.82rem" }}>
                                    {item.text}
                                    {item.link && (
                                        <> <Link to={item.link} className="text-primary ml-1" style={{ fontWeight: 600 }}>{item.linkText} →</Link></>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default AIAnalysisCard;
